import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { MOBILE_UI } from '../../constants/mobileUi';

const FADE = 28;

function edgeMask(fadeStart, fadeEnd) {
  if (!fadeStart && !fadeEnd) return undefined;
  const stops = [
    fadeStart ? `transparent 0, #000 ${FADE}px` : '#000 0',
    fadeEnd ? `#000 calc(100% - ${FADE}px), transparent 100%` : '#000 100%',
  ].join(', ');
  return `linear-gradient(to right, ${stops})`;
}

/**
 * Single-line horizontally scrollable row of `FilterChip`s. Keeps mobile toolbars to one
 * row instead of wrapping stacked control clusters, and fades whichever edge has more
 * chips off-screen so the overflow reads as scrollable rather than clipped.
 *
 * `bleed` cancels the page padding so chips can scroll to the screen edge.
 */
const FilterChipBar = ({
  children,
  sticky = false,
  bleed = MOBILE_UI.pagePadding,
  testId,
  ariaLabel,
}) => {
  const { colors } = useTheme();
  const scrollerRef = useRef(null);
  const [edges, setEdges] = useState({ start: false, end: false });

  const syncEdges = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    const next = {
      start: el.scrollLeft > 2,
      end: maxScroll > 2 && el.scrollLeft < maxScroll - 2,
    };
    setEdges((prev) => (
      prev.start === next.start && prev.end === next.end ? prev : next
    ));
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(syncEdges);
    observer.observe(el);
    return () => observer.disconnect();
  }, [syncEdges]);

  useEffect(() => {
    syncEdges();
  }, [syncEdges, children]);

  const mask = edgeMask(edges.start, edges.end);

  return (
    <div
      ref={scrollerRef}
      onScroll={syncEdges}
      role="group"
      aria-label={ariaLabel}
      data-testid={testId}
      className="mobile-filter-chip-bar"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        overflowX: 'auto',
        overflowY: 'hidden',
        scrollbarWidth: 'none',
        WebkitOverflowScrolling: 'touch',
        padding: `8px ${bleed}px`,
        margin: `0 ${-bleed}px`,
        maskImage: mask,
        WebkitMaskImage: mask,
        ...(sticky ? {
          position: 'sticky',
          top: 0,
          zIndex: 2,
          backgroundColor: colors.bgPrimary,
        } : null),
      }}
    >
      {children}
    </div>
  );
};

export default FilterChipBar;
